import { useEffect, useRef, useState } from 'react';

const SRC_GIS = 'https://accounts.google.com/gsi/client';

interface GoogleAccountsId {
  initialize: (config: {
    client_id: string;
    callback: (res: { credential?: string }) => void;
    cancel_on_tap_outside?: boolean;
  }) => void;
  renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void;
}

declare global {
  interface Window {
    google?: { accounts?: { id?: GoogleAccountsId } };
  }
}

/** Carga el script de Google Identity Services una sola vez por documento. */
function cargarGis(): Promise<void> {
  if (window.google?.accounts?.id) return Promise.resolve();

  const existente = document.querySelector<HTMLScriptElement>(`script[src="${SRC_GIS}"]`);
  if (existente) {
    return new Promise((resolve, reject) => {
      existente.addEventListener('load', () => resolve());
      existente.addEventListener('error', () => reject(new Error('gis')));
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SRC_GIS;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('gis'));
    document.head.appendChild(script);
  });
}

interface Props {
  onCredencial: (idToken: string) => void;
  deshabilitado?: boolean;
  /**
   * Text color variant: 'dark' keeps the carnet portal look (default);
   * 'light' is for the admin panel, where the muted white text is invisible.
   */
  variant?: 'light' | 'dark';
}

/**
 * Botón oficial de Google. El navegador nos entrega un ID token que el backend
 * verifica contra su Client ID, `email_verified` y el dominio de Workspace —
 * aquí no se decide nada sobre la identidad.
 */
export function GoogleSignIn({ onCredencial, deshabilitado, variant = 'dark' }: Props) {
  const contenedor = useRef<HTMLDivElement>(null);
  const [estado, setEstado] = useState<'cargando' | 'listo' | 'error'>('cargando');

  // El callback se guarda en un ref porque GIS lo captura una sola vez, al
  // inicializar, y no volvería a ver una función nueva en cada render.
  const callbackRef = useRef(onCredencial);
  callbackRef.current = onCredencial;

  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID?.toString() ?? '';

  const mutedTextClass = variant === 'light' ? 'text-muted-foreground' : 'text-white/70';
  const loadingTextClass = variant === 'light' ? 'text-muted-foreground' : 'text-white/60';

  useEffect(() => {
    if (!clientId) {
      setEstado('error');
      return;
    }

    let cancelado = false;

    cargarGis()
      .then(() => {
        if (cancelado || !contenedor.current) return;

        const id = window.google?.accounts?.id;
        if (!id) {
          setEstado('error');
          return;
        }

        id.initialize({
          client_id: clientId,
          callback: (res) => {
            if (res.credential) callbackRef.current(res.credential);
          },
          cancel_on_tap_outside: true,
        });

        id.renderButton(contenedor.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          shape: 'pill',
          text: 'continue_with',
          logo_alignment: 'center',
          locale: 'es',
          width: 280,
        });

        setEstado('listo');
      })
      .catch(() => !cancelado && setEstado('error'));

    return () => {
      cancelado = true;
    };
  }, [clientId]);

  if (estado === 'error') {
    return (
      <p className={`max-w-xs text-center text-sm ${mutedTextClass}`}>
        No pudimos cargar el ingreso con Google. Revisa tu conexión y recarga la página.
      </p>
    );
  }

  return (
    <div
      className={
        'flex min-h-[44px] items-center justify-center transition-opacity ' +
        (deshabilitado ? 'pointer-events-none opacity-50' : '')
      }
    >
      {estado === 'cargando' && (
        <span className={`text-sm ${loadingTextClass}`}>Cargando ingreso seguro…</span>
      )}
      <div ref={contenedor} className={estado === 'cargando' ? 'hidden' : ''} />
    </div>
  );
}
